"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface CrosshairPoint {
  x: number;
  y: number;
}

interface ArticleGridShellProps {
  children: React.ReactNode;
  lineColor?: string;
  glowColor?: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
      staggerChildren: 0.1,
    },
  },
};

const lineVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

export function ArticleGridShell({
  children,
  lineColor = "border-white/15",
  glowColor = "border-white/12",
}: ArticleGridShellProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [crosshairs, setCrosshairs] = useState<CrosshairPoint[]>([]);

  const measureLayout = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const section = el.querySelector<HTMLElement>(":scope > section");
    if (!section) return;

    const refRect = el.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const sectionLeft = sectionRect.left - refRect.left;
    const sectionRight = sectionRect.right - refRect.left;
    const sectionTop = sectionRect.top - refRect.top;
    const sectionBottom = sectionRect.bottom - refRect.top;

    // Article section has border-l only (no border-r), so the inner verticals
    // resolve their % against (sectionWidth − 1px). Compute the 1/3 line's
    // visual center from the rect to stay accurate across viewport widths.
    const innerLeft = sectionLeft + 1;
    const innerWidth = sectionRect.width - 1;
    const xs = [
      sectionLeft + 0.5, // border-left center
      innerLeft + innerWidth / 3 + 0.5, // 1/3 line center
      sectionRight - 0.5, // right edge anchor (preserves existing behavior)
    ];

    const points: CrosshairPoint[] = [];
    for (const x of xs) {
      points.push({ x, y: sectionTop + 0.5 });
    }
    for (const x of xs) {
      points.push({ x, y: sectionBottom - 0.5 });
    }

    setCrosshairs(points);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => measureLayout());

    const el = ref.current;
    if (!el) return () => cancelAnimationFrame(raf);

    const section = el.querySelector<HTMLElement>(":scope > section");
    const observer = new ResizeObserver(() => measureLayout());
    observer.observe(el);
    if (section) observer.observe(section);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [measureLayout]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
      el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--mouse-x", "-999px");
    el.style.setProperty("--mouse-y", "-999px");
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}

      {/* Layer 1: Decorative vertical line — fades in on scroll */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-20 hidden lg:block overflow-hidden"
        aria-hidden="true"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div
          variants={lineVariants}
          className={cn("absolute top-0 bottom-0 left-1/3 border-r", lineColor)}
        />
      </motion.div>

      {/* Layer 2: Glow line — cursor-masked */}
      <div
        className="absolute inset-0 z-25 pointer-events-none hidden lg:block"
        aria-hidden="true"
        style={{
          maskImage:
            "radial-gradient(circle 250px at var(--mouse-x, -999px) var(--mouse-y, -999px), black, transparent)",
          WebkitMaskImage:
            "radial-gradient(circle 250px at var(--mouse-x, -999px) var(--mouse-y, -999px), black, transparent)",
        }}
      >
        <div
          className={cn("absolute top-0 bottom-0 left-1/3 border-r", glowColor)}
        />
      </div>

      {/* Layer 3: Crosshairs at section corners */}
      <div
        className="absolute inset-0 z-30 pointer-events-none hidden lg:block overflow-hidden"
        aria-hidden="true"
      >
        {crosshairs.map((point, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${point.x}px`,
              top: `${point.y}px`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <line x1="0" y1="7" x2="14" y2="7" stroke="white" strokeWidth="0.5" />
              <line x1="7" y1="0" x2="7" y2="14" stroke="white" strokeWidth="0.5" />
            </svg>
          </div>
        ))}
      </div>
    </div>
  );
}
